import type { CSSProperties } from 'react'
import { cn } from '@/lib/utils'
import type { AssistantActivityState } from './ai-assistant-status'

const ACTIVE_STATES: ReadonlySet<AssistantActivityState> = new Set([
  'thinking',
  'searching',
  'working',
  'replying',
])

interface TraceDot {
  x: number
  y: number
  sequence: number
  row: number
  column: number
  center?: boolean
}

const TRACE_DOTS: readonly TraceDot[] = [
  { x: 50, y: 10, sequence: 0, row: 0, column: 1 },
  { x: 78, y: 22, sequence: 1, row: 0, column: 2 },
  { x: 90, y: 50, sequence: 2, row: 1, column: 2 },
  { x: 78, y: 78, sequence: 3, row: 2, column: 2 },
  { x: 50, y: 90, sequence: 4, row: 2, column: 1 },
  { x: 22, y: 78, sequence: 5, row: 2, column: 0 },
  { x: 10, y: 50, sequence: 6, row: 1, column: 0 },
  { x: 22, y: 22, sequence: 7, row: 0, column: 0 },
  { x: 50, y: 50, sequence: 8, row: 1, column: 1, center: true },
]

type TraceDotStyle = CSSProperties & {
  '--trace-x': string
  '--trace-y': string
  '--trace-sequence': number
  '--trace-row': number
  '--trace-column': number
}

/**
 * A compact semantic trace for inline assistant activity. Motion is reserved
 * for work that is actually in progress; idle, waiting, and ready render as
 * settled symbols so completion never looks like more work is happening.
 */
export function AgentActivityGlyph({
  state,
  className,
}: {
  state: AssistantActivityState
  className?: string
}) {
  const motion = ACTIVE_STATES.has(state) ? 'active' : 'settled'

  return (
    <span
      aria-hidden="true"
      className={cn('semantic-trace-glyph inline-block size-4 shrink-0', className)}
      data-motion={motion}
      data-slot="agent-activity-glyph"
      data-state={state}
    >
      <span className="semantic-trace-glyph__dots">
        {TRACE_DOTS.map((dot) => (
          <span
            className="semantic-trace-glyph__dot"
            data-center={dot.center ? 'true' : undefined}
            data-slot="semantic-trace-dot"
            key={dot.sequence}
            style={{
              '--trace-x': `${dot.x}%`,
              '--trace-y': `${dot.y}%`,
              '--trace-sequence': dot.sequence,
              '--trace-row': dot.row,
              '--trace-column': dot.column,
            } as TraceDotStyle}
          />
        ))}
      </span>
      <span className="semantic-trace-glyph__settled" />
    </span>
  )
}
